import { useMemo } from "react"

import { UseTableProps, UseTableRes } from "./types"
import { useRowOrder } from "./useRowOrder"
import { useTableBody } from "./useTableBody"
import { useTableColumnWidths } from "./useTableColumnWidths"
import { useTableHeader } from "./useTableHeader"
import { useTableRowSelection } from "./useTableRowSelection"
import { useTableState } from "./useTableState"
import { useListKeyboardNavigation } from "../../../../hooks/useListKeyboardNavigation"
import { useColumnOrder } from "../ColumnDnd/hooks/useColumnOrder"
import { DistributiveOmit, TableFooterBarProps } from "../TableFooterBar/types"

export function useTable<T>({
  data,
  columns: initialColumns,
  cellHeight,
  extractRowKey,
  rowSelectionKind,
  canSelectAllRows = false,
  enableColumnReordering = true,
  lockedColumnsCount = 0,
  onColumnReorder,
  isError = false,
  isLoading = false,
  onClickRow,
  onLastRowInView,
  extractRowHref,
  itemNameInflections,
  tableTestId,
  enableRowHighlighting = true,
  hiddenColumns,
  toggleColumnHideShow,
  emptyPlaceholderMessage,
  initialSelectedByKeys,
  enableRowReordering = false,
  onRowReorder,
  persistColumnWidthsInUrl = true,
  initialColumnWidths,
  hideColumnBorders = false,
  isHeaderSticky = true,
  enableKeyboardNavigation = false,
}: UseTableProps<T>): UseTableRes<T> {
  if (rowSelectionKind === "single-select") {
    canSelectAllRows = false
  }

  const { columns, reorderColumn } = useColumnOrder(
    initialColumns,
    enableColumnReordering,
    lockedColumnsCount,
    onColumnReorder
  )

  const [state, dispatch] = useTableState<T>({
    extractRowKey,
    rowSelectionKind,
    initialSelectedByKeys,
  })

  const { columnWidths, startColumnResize } = useTableColumnWidths({
    columns,
    persistInUrl: persistColumnWidthsInUrl,
    initialColumnWidths,
  })

  const header = useTableHeader({
    data,
    columns,
    columnWidths,
    rowSelectionKind,
    canSelectAllRows,
    state,
    dispatch,
    hiddenColumns,
    toggleColumnHideShow,
    enableColumnReordering,
    reorderColumn,
    enableRowReordering,
    startColumnResize,
    hideColumnBorders,
    isHeaderSticky,
  })

  const body = useTableBody({
    data,
    columns,
    columnWidths,
    extractRowKey,
    rowSelectionKind,
    state,
    dispatch,
    onClickRow,
    extractRowHref,
    enableRowHighlighting,
    hiddenColumns,
    emptyPlaceholderMessage,
    enableRowReordering,
    hideColumnBorders,
  })

  const {
    selected,
    isAllSelected,
    select,
    toggleSelected,
    toggleAllSelected,
    clearSelected,
  } = useTableRowSelection<T>({
    data,
    extractRowKey,
    state,
    dispatch,
  })

  const footerBarProps = useMemo<
    DistributiveOmit<TableFooterBarProps, "actions">
  >(() => {
    if (!rowSelectionKind) {
      return { canSelectRows: false }
    }

    return {
      canSelectRows: true,
      canSelectAllRows,
      numSelectedRows: selected.length,
      itemNameInflections: itemNameInflections ?? { singular: "", plural: "" },
      isAllSelected,
      onToggleAllSelected: toggleAllSelected,
      onClearSelected: clearSelected,
    }
  }, [
    rowSelectionKind,
    canSelectAllRows,
    selected.length,
    itemNameInflections,
    isAllSelected,
    toggleAllSelected,
    clearSelected,
  ])

  const { rowKeys, draggingRowKey, onDragStart, onDragEnd } = useRowOrder({
    data,
    extractRowKey,
    onRowReorder,
  })

  const { searchInputProps, keyboardNavigation } =
    useListKeyboardNavigation<HTMLTableSectionElement>()

  return {
    tableProps: {
      cellHeight,
      header,
      body,
      isError,
      isLoading,
      onLastRowInView,
      testing: { tableTestId },
      rowReordering: {
        rowKeys,
        draggingRowKey,
        onDragStart,
        onDragEnd,
      },
      keyboardNavigation: enableKeyboardNavigation
        ? keyboardNavigation
        : undefined,
      hideColumnBorders,
      isHeaderSticky,
    },
    footerBarProps,
    searchInputProps,
    selected,
    isAllSelected,
    select,
    toggleSelected,
    clearSelected,
    columnWidths,
  }
}
